import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from './Toast';
import './TimesheetFormModal.css';

function TimesheetFormModal({ employeeId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    workDate: '',
    projectId: '',
    hoursWorked: '',
    description: ''
  });
  const [projects, setProjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/projects');
      const projectData = response.data.projects || response.data.data?.projects || [];
      setProjects(projectData);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.workDate) {
      newErrors.workDate = 'Work date is required';
    }

    if (!formData.hoursWorked || parseFloat(formData.hoursWorked) <= 0) {
      newErrors.hoursWorked = 'Hours worked must be a positive number';
    } else if (parseFloat(formData.hoursWorked) > 24) {
      newErrors.hoursWorked = 'Hours worked cannot exceed 24';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors)[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        workDate: formData.workDate,
        hoursWorked: parseFloat(formData.hoursWorked),
        description: formData.description,
        projectId: formData.projectId || null
      };

      await axios.post(`/employees/${employeeId}/timesheets`, payload);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit timesheet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Submit Timesheet</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="workDate">Work Date *</label>
            <input
              type="date"
              id="workDate"
              name="workDate"
              value={formData.workDate}
              onChange={handleChange}
              className={errors.workDate ? 'error' : ''}
              required
            />
            {errors.workDate && <span className="error-message">{errors.workDate}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="projectId">Project</label>
            <select
              id="projectId"
              name="projectId"
              value={formData.projectId}
              onChange={handleChange}
            >
              <option value="">No Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="hoursWorked">Hours Worked *</label>
            <input
              type="number"
              id="hoursWorked"
              name="hoursWorked"
              value={formData.hoursWorked}
              onChange={handleChange}
              className={errors.hoursWorked ? 'error' : ''}
              placeholder="Enter hours worked"
              step="0.5"
              min="0"
              max="24"
              required
            />
            {errors.hoursWorked && <span className="error-message">{errors.hoursWorked}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Enter work description"
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TimesheetFormModal;
